/*
 * forms.js - client-side behaviour for the four enquiry forms.
 *
 * The approved static build submitted with `form.reportValidity()` and never used jQuery; the
 * UI Development Framework v1.2.0 prohibits jQuery outright, so the usual
 * jquery-validation-unobtrusive bundle is deliberately not used (spec 2 / 7.2). Native
 * constraint validation gives the same messages the signed-off pages showed, and server-side
 * validation remains authoritative.
 *
 * On top of that this module fetches the reCAPTCHA v3 token immediately before submitting, so
 * the token is fresh (they expire after two minutes) - spec 9.
 */

const form = document.querySelector('form[method="post"]');

if (form) {
  const tokenField = form.querySelector('#RecaptchaToken');
  const siteKey = tokenField?.dataset.sitekey ?? '';
  const action = tokenField?.dataset.action || 'submit';
  let submitting = false;

  /** Moves focus to the first invalid control so the error is announced (spec 23). */
  const focusFirstInvalid = () => {
    const invalid = form.querySelector(':invalid');
    if (invalid instanceof HTMLElement) {
      invalid.focus({ preventScroll: false });
    }
  };

  const disableSubmit = (disabled) => {
    const button = form.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = disabled;
      button.setAttribute('aria-busy', String(disabled));
    }
  };

  /*
   * Getting a token must NEVER be able to strand the visitor.
   *
   * The first version called event.preventDefault() and then waited on
   * grecaptcha.ready(...).execute(...).finally(submit). Every one of those steps is third-party
   * code: if ready() never invokes its callback, or execute() never settles, or either throws
   * synchronously on a bad key, the .finally never runs - so the form is never submitted, no error
   * is shown, and the click appears to do nothing at all. On a site whose entire job is capturing
   * enquiries, a silent dead end is the worst possible failure.
   *
   * So every path here resolves: errors resolve to a blank token, and a timeout resolves to a
   * blank token too. A blank token is safe - the server treats it as a failed verification and
   * shows the visitor a real message (spec 9) - which means the worst case is a visible error
   * instead of a button that does nothing.
   */
  const TOKEN_TIMEOUT_MS = 8000;

  const requestToken = () => new Promise((resolve) => {
    try {
      window.grecaptcha.ready(() => {
        try {
          // .then(ok, fail) rather than .catch().finally(): no dependency on those methods
          // existing on whatever thenable the API hands back.
          window.grecaptcha.execute(siteKey, { action }).then(
            (token) => resolve(token || ''),
            () => resolve(''),
          );
        } catch {
          resolve('');
        }
      });
    } catch {
      resolve('');
    }
  });

  const submitWithToken = async () => {
    const token = await Promise.race([
      requestToken(),
      new Promise((resolve) => { setTimeout(() => resolve(''), TOKEN_TIMEOUT_MS); }),
    ]);

    if (tokenField) {
      tokenField.value = token;
    }

    submitting = true;
    disableSubmit(false);
    form.submit();
  };

  form.addEventListener('submit', (event) => {
    if (submitting) {
      return;
    }

    if (!form.reportValidity()) {
      event.preventDefault();
      focusFirstInvalid();
      return;
    }

    // No site key configured (local development), or the API never loaded or only half loaded:
    // submit as-is and let the server decide - it fails safe on a missing token.
    // NB both `ready` and `execute` are checked. `typeof window.grecaptcha !== 'undefined'` alone
    // is not enough: a partly-initialised global would pass that and then throw below.
    if (!siteKey
        || typeof window.grecaptcha?.ready !== 'function'
        || typeof window.grecaptcha?.execute !== 'function') {
      return;
    }

    event.preventDefault();
    disableSubmit(true);
    void submitWithToken();
  });

  // --------------------------------------------------- Business type "Other" (sales, 2026-10-06)
  //
  // The free-text box ships `hidden` and is revealed only for "Other". Two things have to move
  // together: the `hidden` attribute (which also takes it out of the accessibility tree) and the
  // `required` attribute. If `required` were left on a hidden control, reportValidity() would block
  // submission pointing at a field nobody can see - the browser refuses to focus it and simply
  // reports "An invalid form control is not focusable", which looks exactly like a dead button.
  //
  // syncBusinessTypeOther() runs on load as well as on change, so a redisplay with Other already
  // selected, or a value restored by the back button, opens the box without the visitor touching it.
  const businessType = form.querySelector('#bd-business-type');
  const otherField = form.querySelector('#bd-business-type-other-field');
  const otherInput = form.querySelector('#bd-business-type-other');

  if (businessType && otherField && otherInput) {
    const syncBusinessTypeOther = () => {
      const show = businessType.value === 'other';

      otherField.hidden = !show;
      otherInput.required = show;

      // Clear on the way out, so switching to Other and back cannot post text that contradicts the
      // choice - the server would reject it anyway, but the email is the thing that matters here.
      if (!show) {
        otherInput.value = '';
      }
    };

    businessType.addEventListener('change', syncBusinessTypeOther);
    syncBusinessTypeOther();
  }

  // If the server re-rendered the form with errors, put the visitor on the first one.
  const firstError = form.querySelector('.field-error.field-validation-error, .validation-summary-errors');
  if (firstError) {
    const field = firstError.closest('.field')?.querySelector('input, textarea, select');
    (field instanceof HTMLElement ? field : firstError).scrollIntoView({ block: 'center' });
    if (field instanceof HTMLElement) {
      field.focus({ preventScroll: true });
    }
  }
}
